import { Bell, Check, X, AlertCircle, Trophy, Users, Calendar } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  created_at: string
  action_url?: string
}

interface NotificationsPanelProps {
  notifications: Notification[]
  onMarkAsRead?: (id: string) => void
  onMarkAllAsRead?: () => void
}

export function NotificationsPanel({ 
  notifications, 
  onMarkAsRead,
  onMarkAllAsRead 
}: NotificationsPanelProps) {
  const mockNotifications: Notification[] = [
    {
      id: '1',
      title: 'Security Update Applied',
      message: 'Your account security has been enhanced with new protections',
      type: 'success',
      read: false,
      created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString()
    },
    {
      id: '2', 
      title: 'Draft Reminder',
      message: 'Premier League Draft starts in 45 minutes - prepare your strategy!',
      type: 'warning',
      read: false,
      created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString()
    },
    {
      id: '3',
      title: 'Achievement Unlocked',
      message: 'Congratulations! You\'ve reached Legend Tier',
      type: 'success',
      read: false,
      created_at: new Date(Date.now() - 45 * 60 * 1000).toISOString()
    },
    {
      id: '4',
      title: 'Lineup Update Required',
      message: 'Key player injured - update your formation before kickoff',
      type: 'error',
      read: false,
      created_at: new Date(Date.now() - 90 * 60 * 1000).toISOString()
    },
    {
      id: '5',
      title: 'New League Invitation',
      message: 'You\'ve been invited to join "Elite Champions League"',
      type: 'info',
      read: true,
      created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    }
  ]

  const displayNotifications = notifications.length > 0 ? notifications : mockNotifications
  const unreadCount = displayNotifications.filter(n => !n.read).length

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <Trophy className="h-4 w-4 text-primary" />
      case 'warning':
        return <Calendar className="h-4 w-4 text-warning" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />
    }
  }

  const getTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60)
    
    if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)} minutes ago`
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} hours ago`
    } else {
      return `${Math.floor(diffInMinutes / 1440)} days ago`
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Notifications
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {unreadCount}
              </Badge>
            )}
          </CardTitle>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onMarkAllAsRead}
              className="text-xs"
            >
              Mark all read
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {displayNotifications.slice(0, 6).map((notification) => (
            <div 
              key={notification.id}
              className={`p-3 rounded-lg border transition-colors ${
                !notification.read 
                  ? 'bg-primary/5 border-primary/20' 
                  : 'bg-muted/30 border-transparent'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  {getIcon(notification.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-medium text-sm line-clamp-1">
                      {notification.title}
                    </h4>
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 shrink-0"
                        onClick={() => onMarkAsRead?.(notification.id)}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                    {notification.message}
                  </p>
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    {getTimeAgo(notification.created_at)}
                  </p>
                </div>
              </div>
            </div>
          ))}
          
          {displayNotifications.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No notifications</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}